import { useState, useEffect, useCallback, type FormEvent } from 'react'
import { Copy, Check, ExternalLink, Mail, Pencil, Plus, Star, Trash2 } from 'lucide-react'
import { useAuthStore } from '@/lib/auth-store'
import { apiFetch, ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { GROUP_LABELS } from '@/lib/roles'
import { PageHeader } from '@/components/PageHeader'
import { HelixVaultLink } from '@/components/HelixVaultLink'

interface Mailbox {
  id: string
  address: string
  label: string
  description: string | null
  isDefault: boolean
  isActive: boolean
  _count?: { rules: number }
}

interface CustomRole {
  id: string
  name: string
  slug: string
  group: string
  description: string | null
}

const ROLE_GROUPS = [
  'artist_and_music',
  'management_and_ops',
  'sound',
  'lighting_and_visuals',
  'stage_and_backline',
  'wardrobe_and_personal',
  'local_production',
  'venue_staff',
] as const

export function SettingsPage() {
  const user = useAuthStore((s) => s.user)
  const setUser = useAuthStore((s) => s.setUser)
  const [dateFormat, setDateFormat] = useState(
    (user as Record<string, unknown> | null)?.dateFormat as string ?? 'en-GB',
  )
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [copied, setCopied] = useState<string | null>(null)

  // ── Custom roles state ──
  const [customRoles, setCustomRoles] = useState<CustomRole[]>([])
  const [rolesLoading, setRolesLoading] = useState(true)
  const [roleError, setRoleError] = useState<string | null>(null)
  const [newRoleName, setNewRoleName] = useState('')
  const [newRoleGroup, setNewRoleGroup] = useState<string>(ROLE_GROUPS[0])
  const [newRoleDesc, setNewRoleDesc] = useState('')
  const [addingRole, setAddingRole] = useState(false)
  const [deletingRoleId, setDeletingRoleId] = useState<string | null>(null)

  const loadCustomRoles = useCallback(async () => {
    try {
      setRolesLoading(true)
      setRoleError(null)
      const data = await artistFetch<CustomRole[]>('/custom-roles')
      setCustomRoles(data)
    } catch (err) {
      setRoleError(err instanceof ApiClientError ? err.message : 'Failed to load custom roles')
    } finally {
      setRolesLoading(false)
    }
  }, [])

  useEffect(() => {
    loadCustomRoles()
  }, [loadCustomRoles])

  async function addCustomRole() {
    if (!newRoleName.trim()) return
    setAddingRole(true)
    setRoleError(null)
    try {
      await artistFetch('/custom-roles', {
        method: 'POST',
        body: {
          name: newRoleName.trim(),
          group: newRoleGroup,
          ...(newRoleDesc.trim() ? { description: newRoleDesc.trim() } : {}),
        },
      })
      setNewRoleName('')
      setNewRoleDesc('')
      setNewRoleGroup(ROLE_GROUPS[0])
      await loadCustomRoles()
    } catch (err) {
      setRoleError(err instanceof ApiClientError ? err.message : 'Failed to create role')
    } finally {
      setAddingRole(false)
    }
  }

  async function deleteCustomRole(id: string) {
    setDeletingRoleId(id)
    setRoleError(null)
    try {
      await artistFetch(`/custom-roles/${id}`, { method: 'DELETE' })
      await loadCustomRoles()
    } catch (err) {
      setRoleError(err instanceof ApiClientError ? err.message : 'Failed to delete role')
    } finally {
      setDeletingRoleId(null)
    }
  }

  // ── Mailboxes state ──
  const [mailboxes, setMailboxes] = useState<Mailbox[]>([])
  const [mailboxesLoading, setMailboxesLoading] = useState(true)
  const [mailboxError, setMailboxError] = useState<string | null>(null)
  const [newMbAddress, setNewMbAddress] = useState('')
  const [newMbLabel, setNewMbLabel] = useState('')
  const [newMbDesc, setNewMbDesc] = useState('')
  const [addingMailbox, setAddingMailbox] = useState(false)

  const loadMailboxes = useCallback(async () => {
    try {
      setMailboxesLoading(true)
      setMailboxError(null)
      const data = await artistFetch<Mailbox[]>('/mailboxes')
      setMailboxes(data)
    } catch (err) {
      setMailboxError(err instanceof ApiClientError ? err.message : 'Failed to load mailboxes')
    } finally {
      setMailboxesLoading(false)
    }
  }, [])

  useEffect(() => {
    loadMailboxes()
  }, [loadMailboxes])

  async function addMailbox(e: FormEvent) {
    e.preventDefault()
    if (!newMbAddress.trim() || !newMbLabel.trim()) return
    setAddingMailbox(true)
    setMailboxError(null)
    try {
      await artistFetch('/mailboxes', {
        method: 'POST',
        body: {
          address: newMbAddress.trim(),
          label: newMbLabel.trim(),
          ...(newMbDesc.trim() ? { description: newMbDesc.trim() } : {}),
        },
      })
      setNewMbAddress('')
      setNewMbLabel('')
      setNewMbDesc('')
      await loadMailboxes()
    } catch (err) {
      setMailboxError(err instanceof ApiClientError ? err.message : 'Failed to create mailbox')
    } finally {
      setAddingMailbox(false)
    }
  }

  async function deleteMailbox(id: string) {
    if (!confirm('Delete this mailbox? Its rules will become global.')) return
    try {
      await artistFetch(`/mailboxes/${id}`, { method: 'DELETE' })
      await loadMailboxes()
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Failed to delete mailbox')
    }
  }

  async function setDefaultMailbox(id: string) {
    try {
      await artistFetch(`/mailboxes/${id}/set-default`, { method: 'POST' })
      await loadMailboxes()
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Failed to set default')
    }
  }

  const artistSlug = (user as Record<string, unknown> | null)?.artistSlug as string | undefined
  const artistName = (user as Record<string, unknown> | null)?.artistName as string | undefined

  // Slug editing
  const [editingSlug, setEditingSlug] = useState(false)
  const [newSlug, setNewSlug] = useState(artistSlug ?? '')
  const [slugSaving, setSlugSaving] = useState(false)
  const [slugError, setSlugError] = useState<string | null>(null)

  async function saveSlug() {
    const slug = newSlug.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '')
    if (!slug) return
    setSlugSaving(true)
    setSlugError(null)
    try {
      await apiFetch('/auth/artist', { method: 'PATCH', body: { slug } })
      // Refresh user session to pick up new slug
      const me = await apiFetch<any>('/auth/me')
      if (user && me) setUser({ ...user, ...me } as typeof user)
      setEditingSlug(false)
    } catch (err) {
      setSlugError(err instanceof ApiClientError ? err.message : 'Failed to update slug')
    } finally {
      setSlugSaving(false)
    }
  }

  const helixVaultUrl = user?.helixVaultUrl
  const helixVaultOverride = user?.helixVaultUrlOverride
  const isArtistAdmin = user?.role === 'admin'
  const [editingVault, setEditingVault] = useState(false)
  const [vaultUrlInput, setVaultUrlInput] = useState('')
  const [vaultSaving, setVaultSaving] = useState(false)
  const [vaultError, setVaultError] = useState<string | null>(null)

  async function saveVaultUrl() {
    const trimmed = vaultUrlInput.trim()
    setVaultSaving(true)
    setVaultError(null)
    try {
      const res = await apiFetch<{
        helixVaultUrl?: string
        helixVaultUrlOverride?: string | null
      }>('/auth/artist', {
        method: 'PATCH',
        body: { helixVaultUrl: trimmed ? trimmed : null },
      })
      if (user) {
        const next = {
          ...user,
          helixVaultUrlOverride: res.helixVaultUrlOverride ?? null,
        }
        if (res.helixVaultUrl) {
          next.helixVaultUrl = res.helixVaultUrl
        } else {
          delete next.helixVaultUrl
        }
        setUser(next)
      }
      setEditingVault(false)
    } catch (err) {
      setVaultError(err instanceof ApiClientError ? err.message : 'Failed to update Helix Vault link')
    } finally {
      setVaultSaving(false)
    }
  }

  async function clearVaultOverride() {
    setVaultSaving(true)
    setVaultError(null)
    try {
      const res = await apiFetch<{
        helixVaultUrl?: string
        helixVaultUrlOverride?: string | null
      }>('/auth/artist', {
        method: 'PATCH',
        body: { helixVaultUrl: null },
      })
      if (user) {
        const next = {
          ...user,
          helixVaultUrlOverride: null,
        }
        if (res.helixVaultUrl) {
          next.helixVaultUrl = res.helixVaultUrl
        } else {
          delete next.helixVaultUrl
        }
        setUser(next)
      }
      setVaultUrlInput('')
      setEditingVault(false)
    } catch (err) {
      setVaultError(err instanceof ApiClientError ? err.message : 'Failed to clear vault URL')
    } finally {
      setVaultSaving(false)
    }
  }

  const apiBaseUrl = window.location.hostname === 'localhost'
    ? 'http://localhost:3001'
    : 'https://api.ovationhub.net'
  const publicInquiryUrl = artistSlug
    ? `${apiBaseUrl}/public/inquiries/${artistSlug}`
    : null

  function copyToClipboard(text: string, label: string) {
    navigator.clipboard.writeText(text)
    setCopied(label)
    setTimeout(() => setCopied(null), 2000)
  }

  async function onSave() {
    setSaving(true)
    setSaved(false)
    try {
      if (user) {
        setUser({ ...user, dateFormat } as typeof user)
      }
      setSaved(true)
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <PageHeader
        title="Settings"
        subtitle="Artist configuration, integration endpoints, and personal preferences."
      />

      <div className="max-w-2xl space-y-6">
        {/* ── Artist identity ── */}
        <div className="rounded-lg border border-ovation-ink/10 bg-white p-6">
          <h2 className="mb-4 font-display text-xl">Artist identity</h2>
          <dl className="space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <dt className="text-ovation-muted">Artist name</dt>
              <dd className="font-medium">{artistName ?? '—'}</dd>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <dt className="text-ovation-muted">Artist slug</dt>
                <dd className="flex items-center gap-2">
                  {editingSlug ? (
                    <div className="flex items-center gap-2">
                      <input
                        value={newSlug}
                        onChange={(e) => setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                        className="rounded-md border border-ovation-ink/15 px-2 py-1 text-xs font-mono w-48"
                        placeholder="artist-slug"
                      />
                      <button
                        type="button"
                        onClick={saveSlug}
                        disabled={slugSaving || !newSlug.trim()}
                        className="rounded bg-ovation-ink px-2 py-1 text-xs text-ovation-paper disabled:opacity-50"
                      >
                        {slugSaving ? '...' : 'Save'}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setEditingSlug(false); setSlugError(null) }}
                        className="rounded border border-ovation-ink/15 px-2 py-1 text-xs"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <>
                      <code className="rounded bg-ovation-ink/5 px-2 py-0.5 text-xs font-mono">
                        {artistSlug ?? '—'}
                      </code>
                      {artistSlug && (
                        <button
                          type="button"
                          onClick={() => copyToClipboard(artistSlug, 'slug')}
                          className="rounded border border-ovation-ink/15 p-1 hover:bg-ovation-ink/5"
                          title="Copy slug"
                        >
                          {copied === 'slug' ? (
                            <Check className="h-3 w-3 text-emerald-600" />
                          ) : (
                            <Copy className="h-3 w-3 text-ovation-muted" />
                          )}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => { setNewSlug(artistSlug ?? ''); setEditingSlug(true) }}
                        className="rounded border border-ovation-ink/15 p-1 hover:bg-ovation-ink/5"
                        title="Edit slug"
                      >
                        <Pencil className="h-3 w-3 text-ovation-muted" />
                      </button>
                    </>
                  )}
                </dd>
              </div>
              {slugError && (
                <p className="mt-1 text-xs text-red-600">{slugError}</p>
              )}
              <p className="mt-1 text-xs text-ovation-muted">
                Used in public URLs and integrations. Lowercase, hyphens only. Must be unique.
              </p>
            </div>
          </dl>
        </div>

        {/* ── Helix Vault (outbound link only) ── */}
        <div className="rounded-lg border border-ovation-ink/10 bg-white p-6">
          <h2 className="mb-2 font-display text-xl">Helix Vault</h2>
          <p className="mb-4 text-sm text-ovation-muted">
            Artist assets and documents live in Helix under the Geeky Bee ecosystem. Ovation does not
            sync or share data with Helix — this is a plain web link to the vault.
          </p>

          {helixVaultUrl ? (
            <div className="mb-4">
              <HelixVaultLink url={helixVaultUrl} />
            </div>
          ) : (
            <p className="mb-4 text-sm text-ovation-muted">
              No vault link yet. Set <code className="text-xs">HELIX_VAULT_BASE_URL</code> on the API
              (e.g. <code className="text-xs">https://helix.geekybee.net/vault</code>) or add a custom
              URL below.
            </p>
          )}

          {vaultError && (
            <p className="mb-3 text-xs text-red-600">{vaultError}</p>
          )}

          {isArtistAdmin ? (
            editingVault ? (
              <div className="space-y-3">
                <label className="block text-sm text-ovation-muted">
                  Custom vault URL (optional — overrides platform default)
                </label>
                <input
                  type="url"
                  value={vaultUrlInput}
                  onChange={(e) => setVaultUrlInput(e.target.value)}
                  placeholder="https://helix.geekybee.net/vault/your-artist"
                  className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm font-mono"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={saveVaultUrl}
                    disabled={vaultSaving}
                    className="rounded bg-ovation-ink px-3 py-1.5 text-sm text-ovation-paper disabled:opacity-50"
                  >
                    {vaultSaving ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setEditingVault(false); setVaultError(null) }}
                    className="rounded border border-ovation-ink/15 px-3 py-1.5 text-sm"
                  >
                    Cancel
                  </button>
                  {helixVaultOverride && (
                    <button
                      type="button"
                      onClick={clearVaultOverride}
                      disabled={vaultSaving}
                      className="rounded border border-ovation-ink/15 px-3 py-1.5 text-sm text-ovation-muted"
                    >
                      Clear custom URL
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setVaultUrlInput(helixVaultOverride ?? helixVaultUrl ?? '')
                  setEditingVault(true)
                  setVaultError(null)
                }}
                className="rounded border border-ovation-ink/15 px-3 py-1.5 text-sm hover:bg-ovation-ink/5"
              >
                {helixVaultOverride ? 'Edit custom vault URL' : 'Set custom vault URL'}
              </button>
            )
          ) : (
            <p className="text-xs text-ovation-muted">
              Only artist admins can change the vault link.
            </p>
          )}
        </div>

        {/* ── Integration endpoints ── */}
        <div className="rounded-lg border border-ovation-ink/10 bg-white p-6">
          <h2 className="mb-2 font-display text-xl">Integration endpoints</h2>
          <p className="mb-4 text-sm text-ovation-muted">
            Use these URLs to connect external systems (WordPress, custom forms, webhooks) to Ovation.
          </p>

          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-ovation-muted">
                Public inquiry endpoint
              </label>
              <p className="mb-1 text-xs text-ovation-muted">
                POST booking requests here from your website form. No authentication required.
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 rounded bg-ovation-ink/5 px-3 py-2 text-xs font-mono break-all">
                  {publicInquiryUrl ?? 'Set artist slug first'}
                </code>
                {publicInquiryUrl && (
                  <button
                    type="button"
                    onClick={() => copyToClipboard(publicInquiryUrl, 'url')}
                    className="shrink-0 rounded border border-ovation-ink/15 p-1.5 hover:bg-ovation-ink/5"
                    title="Copy URL"
                  >
                    {copied === 'url' ? (
                      <Check className="h-3.5 w-3.5 text-emerald-600" />
                    ) : (
                      <Copy className="h-3.5 w-3.5 text-ovation-muted" />
                    )}
                  </button>
                )}
              </div>
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-ovation-muted">
                Inbound email webhook
              </label>
              <p className="mb-1 text-xs text-ovation-muted">
                Configure your email provider (Brevo) to forward inbound emails here.
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 rounded bg-ovation-ink/5 px-3 py-2 text-xs font-mono break-all">
                  {apiBaseUrl}/webhooks/inbound-email
                </code>
                <button
                  type="button"
                  onClick={() => copyToClipboard(`${apiBaseUrl}/webhooks/inbound-email`, 'webhook')}
                  className="shrink-0 rounded border border-ovation-ink/15 p-1.5 hover:bg-ovation-ink/5"
                  title="Copy URL"
                >
                  {copied === 'webhook' ? (
                    <Check className="h-3.5 w-3.5 text-emerald-600" />
                  ) : (
                    <Copy className="h-3.5 w-3.5 text-ovation-muted" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ── Mailboxes ── */}
        <div className="rounded-lg border border-ovation-ink/10 bg-white p-6">
          <h2 className="mb-2 font-display text-xl">Mailboxes</h2>
          <p className="mb-4 text-sm text-ovation-muted">
            Configure inbound email addresses. Each mailbox can have its own independent routing rules.
          </p>

          {mailboxError && (
            <div className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
              {mailboxError}
            </div>
          )}

          {/* Add mailbox form */}
          <form onSubmit={addMailbox} className="mb-6 space-y-3 rounded-md border border-ovation-ink/10 bg-ovation-ink/[0.02] p-4">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Plus className="h-4 w-4" />
              Add mailbox
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-ovation-muted">
                  Email address
                </label>
                <input
                  type="email"
                  value={newMbAddress}
                  onChange={(e) => setNewMbAddress(e.target.value)}
                  placeholder="bookings@example.com"
                  className="w-full rounded-md border border-ovation-ink/15 px-3 py-1.5 text-sm"
                  required
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-ovation-muted">
                  Label
                </label>
                <input
                  value={newMbLabel}
                  onChange={(e) => setNewMbLabel(e.target.value)}
                  placeholder="e.g. Bookings"
                  className="w-full rounded-md border border-ovation-ink/15 px-3 py-1.5 text-sm"
                  required
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-ovation-muted">
                  Description (optional)
                </label>
                <input
                  value={newMbDesc}
                  onChange={(e) => setNewMbDesc(e.target.value)}
                  placeholder="What this mailbox is for"
                  className="w-full rounded-md border border-ovation-ink/15 px-3 py-1.5 text-sm"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={addingMailbox || !newMbAddress.trim() || !newMbLabel.trim()}
              className="rounded-md bg-ovation-ink px-4 py-1.5 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
            >
              {addingMailbox ? 'Adding...' : 'Add mailbox'}
            </button>
          </form>

          {/* Mailbox list */}
          {mailboxesLoading ? (
            <p className="text-sm text-ovation-muted">Loading mailboxes...</p>
          ) : mailboxes.length === 0 ? (
            <p className="text-sm text-ovation-muted">No mailboxes configured. Add one above to enable per-mailbox routing.</p>
          ) : (
            <div className="space-y-1">
              {mailboxes.map((mb) => (
                <div
                  key={mb.id}
                  className="flex items-center justify-between rounded-md border border-ovation-ink/10 px-3 py-2"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <Mail className="h-3.5 w-3.5 text-ovation-muted shrink-0" />
                      <span className="text-sm font-medium">{mb.label}</span>
                      <code className="rounded bg-ovation-ink/5 px-1.5 py-0.5 text-xs font-mono text-ovation-muted">
                        {mb.address}
                      </code>
                      {mb.isDefault && (
                        <span className="flex items-center gap-1 rounded-full bg-yellow-100 px-2 py-0.5 text-[10px] font-medium text-yellow-800">
                          <Star className="h-2.5 w-2.5" /> Default
                        </span>
                      )}
                      {!mb.isActive && (
                        <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-medium text-red-700">
                          Inactive
                        </span>
                      )}
                      <span className="text-xs text-ovation-muted">
                        {mb._count?.rules ?? 0} rule{(mb._count?.rules ?? 0) !== 1 ? 's' : ''}
                      </span>
                    </div>
                    {mb.description && (
                      <p className="mt-0.5 ml-5.5 text-xs text-ovation-muted truncate">{mb.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0 ml-2">
                    <a
                      href={`/email-rules?mailbox=${mb.id}`}
                      className="rounded border border-ovation-ink/15 p-1 hover:bg-ovation-ink/5 text-ovation-muted hover:text-ovation-ink"
                      title="View rules for this mailbox"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                    {!mb.isDefault && (
                      <button
                        type="button"
                        onClick={() => setDefaultMailbox(mb.id)}
                        title="Set as default catch-all"
                        className="rounded border border-ovation-ink/15 p-1 hover:bg-yellow-50 text-ovation-muted hover:text-yellow-600"
                      >
                        <Star className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => deleteMailbox(mb.id)}
                      className="rounded border border-ovation-ink/15 p-1 hover:bg-red-50 text-ovation-muted hover:text-red-600"
                      title="Delete mailbox"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Custom roles ── */}
        <div className="rounded-lg border border-ovation-ink/10 bg-white p-6">
          <h2 className="mb-2 font-display text-xl">Custom roles</h2>
          <p className="mb-4 text-sm text-ovation-muted">
            Add roles specific to your act. Custom roles appear in all role dropdowns alongside system defaults.
          </p>

          {roleError && (
            <div className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
              {roleError}
            </div>
          )}

          {/* Add form */}
          <div className="mb-6 space-y-3 rounded-md border border-ovation-ink/10 bg-ovation-ink/[0.02] p-4">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Plus className="h-4 w-4" />
              Add custom role
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-ovation-muted">
                  Name
                </label>
                <input
                  value={newRoleName}
                  onChange={(e) => setNewRoleName(e.target.value)}
                  placeholder="e.g. Pyrotechnics Operator"
                  className="w-full rounded-md border border-ovation-ink/15 px-3 py-1.5 text-sm"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-ovation-muted">
                  Group
                </label>
                <select
                  value={newRoleGroup}
                  onChange={(e) => setNewRoleGroup(e.target.value)}
                  className="w-full rounded-md border border-ovation-ink/15 px-3 py-1.5 text-sm bg-white"
                >
                  {ROLE_GROUPS.map((g) => (
                    <option key={g} value={g}>{GROUP_LABELS[g] ?? g}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-ovation-muted">
                Description (optional)
              </label>
              <input
                value={newRoleDesc}
                onChange={(e) => setNewRoleDesc(e.target.value)}
                placeholder="Brief description of this role"
                className="w-full rounded-md border border-ovation-ink/15 px-3 py-1.5 text-sm"
              />
            </div>
            <button
              type="button"
              onClick={addCustomRole}
              disabled={addingRole || !newRoleName.trim()}
              className="rounded-md bg-ovation-ink px-4 py-1.5 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
            >
              {addingRole ? 'Adding...' : 'Add role'}
            </button>
          </div>

          {/* Existing custom roles grouped by ShowRoleGroup */}
          {rolesLoading ? (
            <p className="text-sm text-ovation-muted">Loading custom roles...</p>
          ) : customRoles.length === 0 ? (
            <p className="text-sm text-ovation-muted">No custom roles yet. Add one above.</p>
          ) : (
            <div className="space-y-4">
              {Object.entries(GROUP_LABELS)
                .filter(([key]) => customRoles.some((r) => r.group === key))
                .map(([groupKey, groupLabel]) => (
                  <div key={groupKey}>
                    <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-ovation-muted">
                      {groupLabel}
                    </h3>
                    <div className="space-y-1">
                      {customRoles
                        .filter((r) => r.group === groupKey)
                        .map((role) => (
                          <div
                            key={role.id}
                            className="flex items-center justify-between rounded-md border border-ovation-ink/10 px-3 py-2"
                          >
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium">{role.name}</span>
                                <span className="rounded bg-ovation-ink/5 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-ovation-muted">
                                  {GROUP_LABELS[role.group] ?? role.group}
                                </span>
                              </div>
                              {role.description && (
                                <p className="mt-0.5 text-xs text-ovation-muted truncate">
                                  {role.description}
                                </p>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => deleteCustomRole(role.id)}
                              disabled={deletingRoleId === role.id}
                              className="ml-2 shrink-0 rounded border border-ovation-ink/15 p-1 hover:bg-red-50 hover:border-red-200 disabled:opacity-50"
                              title="Delete role"
                            >
                              <Trash2 className="h-3.5 w-3.5 text-ovation-muted hover:text-red-600" />
                            </button>
                          </div>
                        ))}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>

        {/* ── Display preferences ── */}
        <div className="rounded-lg border border-ovation-ink/10 bg-white p-6">
          <h2 className="mb-4 font-display text-xl">Display preferences</h2>

          <div className="space-y-4">
            <div>
              <label className="mb-2 block text-sm font-medium">
                Date format
              </label>
              <div className="space-y-2">
                {[
                  { value: 'en-GB', label: 'DD/MM/YYYY', example: '10/04/2026' },
                  { value: 'en-US', label: 'MM/DD/YYYY', example: '04/10/2026' },
                  { value: 'iso', label: 'YYYY-MM-DD', example: '2026-04-10' },
                ].map((opt) => (
                  <label
                    key={opt.value}
                    className="flex items-center gap-3 rounded-md border border-ovation-ink/10 px-3 py-2 cursor-pointer hover:bg-ovation-ink/5"
                  >
                    <input
                      type="radio"
                      name="dateFormat"
                      value={opt.value}
                      checked={dateFormat === opt.value}
                      onChange={() => setDateFormat(opt.value)}
                      className="accent-ovation-ink"
                    />
                    <div>
                      <div className="text-sm font-medium">{opt.label}</div>
                      <div className="text-xs text-ovation-muted">
                        Example: {opt.example}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <button
              type="button"
              onClick={onSave}
              disabled={saving}
              className="rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save preferences'}
            </button>
            {saved && (
              <div className="text-sm text-emerald-700">
                Preferences saved.
              </div>
            )}
          </div>
        </div>

        {/* ── Account ── */}
        <div className="rounded-lg border border-ovation-ink/10 bg-white p-6">
          <h2 className="mb-4 font-display text-xl">Account</h2>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-ovation-muted">Email</dt>
              <dd>{user?.email ?? '—'}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-ovation-muted">Display name</dt>
              <dd>{user?.displayName ?? '—'}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-ovation-muted">Account type</dt>
              <dd className="capitalize">{user?.accountType ?? '—'}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-ovation-muted">Email verified</dt>
              <dd>{user?.emailVerified ? 'Yes' : 'No'}</dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  )
}
